import { useSyncExternalStore } from "react";

/**
 * Subscribe to a CSS media query from React.
 *
 * `useSyncExternalStore` rather than `useState` + an effect: the value is read
 * DURING render from the same source the browser is already using, so there is
 * no first paint at the wrong breakpoint. `hooks/use-mobile.tsx` takes the other
 * approach and returns `false` on the first render no matter the window size,
 * which is fine for hiding a sidebar and not fine for choosing a dialog's
 * shape — a centred card that becomes a bottom sheet one frame later is a
 * visible flinch on every open.
 *
 * The third argument is a server snapshot. Nothing here renders on a server,
 * but it is what stops the hook throwing under a non-DOM test environment where
 * `matchMedia` does not exist.
 */
export function useMediaQuery(query: string): boolean {
  const store = storeFor(query);
  return useSyncExternalStore(store.subscribe, store.getSnapshot, serverSnapshot);
}

/* One store per query, shared by every caller (K11). The subscribe function
   used to be an inline arrow, and useSyncExternalStore re-subscribes whenever
   its identity changes — so every render of every component reading a
   breakpoint tore down its `change` listener and added a new one, and each
   read built a fresh MediaQueryList. Cached here, a query is one list and one
   listener set for the life of the page. */
type Store = { subscribe: (cb: () => void) => () => void; getSnapshot: () => boolean };
const stores = new Map<string, Store>();
const serverSnapshot = () => false;

function storeFor(query: string): Store {
  let store = stores.get(query);
  if (store) return store;
  const hasDom = typeof window !== "undefined" && typeof window.matchMedia === "function";
  const mql = hasDom ? window.matchMedia(query) : null;
  store = {
    subscribe: (onChange) => {
      if (!mql) return () => {};
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    },
    getSnapshot: () => (mql ? mql.matches : false),
  };
  stores.set(query, store);
  return store;
}

/**
 * Named breakpoints, matching `tailwind.config.ts`. Read these rather than
 * writing raw widths at call sites, so the breakpoint set stays one decision.
 *
 * `usePhone` is the `sm` boundary and deliberately NOT "is it touch": a modal
 * becomes a sheet because the window is narrow, not because the pointer is a
 * finger. An iPad at 1024px takes the centred dialog.
 */
export const usePhone = () => useMediaQuery("(max-width: 639.98px)");
export const useTablet = () => useMediaQuery("(min-width: 768px)");
export const useDesktop = () => useMediaQuery("(min-width: 1024px)");
/** The navigation split — the same query, character for character, as the
    `sidenav:` variant in client/src/index.css and the `html.has-sidebar`
    block that publishes --sidebar-space (tests/navSplit.test.ts compares
    them). The docked sidebar from 768px, EXCEPT a window in portrait
    below 1024px — an iPad mini / Air / 11" upright, with or without a
    keyboard and trackpad — which gets the phone's tab bar (Andrew,
    2026-10-31: at 768 the sidebar took ~37% of the screen and toolbars
    wrapped; 2026-11-02: "tablet portrait should show the mobile menu", so
    the pointer no longer decides). Landscape iPads and the 12.9" upright
    (1024 wide) keep the sidebar. Written as the cases that make up the exact
    complement of `tabnav:`, so no `not` is needed. */
export const SIDE_NAV_QUERY =
  "(min-width: 1024px), (min-width: 768px) and (orientation: landscape)";
/** `tabnav:` — the tab bar is the navigation. The exact complement of the above. */
export const TAB_NAV_QUERY =
  "(max-width: 767.98px), (max-width: 1023.98px) and (orientation: portrait)";
export const useSideNav = () => useMediaQuery(SIDE_NAV_QUERY);
/** Whether a settings area (Platform / Company) opens as a layer over the
    page rather than as a page. A WIDTH, not the navigation split: an upright
    iPad has the tab bar but still has room for the modal round the section,
    and rotating one with the modal open must not turn it into a page. */
export const LAYER_QUERY = "(min-width: 768px)";

/** True when the primary input is a finger — drives hit-target and hover rules. */
export const useCoarsePointer = () => useMediaQuery("(pointer: coarse)");

/**
 * Whether it is safe to focus a field the moment something opens.
 *
 * On a touch device it is not: focusing an input summons the on-screen
 * keyboard, which on a phone covers roughly half the screen — so a sheet that
 * autofocuses its first field is a sheet the reader never sees.
 *
 * Keyed on POINTER, not width. The question is "will focusing this raise a
 * keyboard", and the answer follows the input device: an iPad at 1024px has the
 * same problem as a phone, and a narrow desktop window has none. Width is the
 * wrong axis and would get both cases backwards.
 */
export const useAutoFocus = () => !useCoarsePointer();

/** Honour the reader's motion preference before animating anything large. */
export const usePrefersReducedMotion = () => useMediaQuery("(prefers-reduced-motion: reduce)");
